import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import type { ReactElement } from 'react';

interface MarkdownBlockProps {
    children: string;
}

/**
 * Scopes markdown typography down to the conversation bubble's `xs` scale: tight
 * inter-block spacing, trimmed first/last margins so the bubble padding stays
 * snug, and theme-agnostic fills for code/quotes that read on any bubble surface.
 */
const MarkdownScope = styled.div`
    font-size: var(--mantine-font-size-xs);
    line-height: 1.55;
    word-break: break-word;

    > *:first-child {
        margin-top: 0;
    }
    > *:last-child {
        margin-bottom: 0;
    }

    p {
        margin: 0 0 0.5em;
    }

    ul,
    ol {
        margin: 0 0 0.5em;
        padding-left: 1.25em;
    }
    li {
        margin: 0.15em 0;
    }
    li > p {
        margin: 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 0.6em 0 0.3em;
        line-height: 1.3;
        font-weight: 700;
    }
    h1 {
        font-size: 1.3em;
    }
    h2 {
        font-size: 1.2em;
    }
    h3 {
        font-size: 1.1em;
    }
    h4,
    h5,
    h6 {
        font-size: 1em;
    }

    a {
        color: var(--mantine-primary-color-filled);
        text-decoration: underline;
    }

    code {
        font-family: var(--mantine-font-family-monospace);
        font-size: 0.9em;
        background: var(--bm-color-surface-alt);
        padding: 0.1em 0.3em;
        border-radius: calc(var(--bm-radius) / 2);
    }

    pre {
        margin: 0 0 0.5em;
        padding: 0.6em 0.7em;
        overflow-x: auto;
        background: var(--bm-color-surface-alt);
        border-radius: var(--bm-radius);
    }
    pre code {
        background: none;
        padding: 0;
    }

    blockquote {
        margin: 0 0 0.5em;
        padding-left: 0.7em;
        border-left: 2px solid var(--bm-color-border-subtle);
        color: var(--mantine-color-dimmed);
    }

    table {
        border-collapse: collapse;
        margin: 0 0 0.5em;
        font-size: 0.95em;
    }
    th,
    td {
        border: 1px solid var(--bm-color-border-subtle);
        padding: 0.25em 0.5em;
        text-align: left;
    }

    img {
        max-width: 100%;
    }

    hr {
        border: none;
        border-top: 1px solid var(--bm-color-border-subtle);
        margin: 0.6em 0;
    }
`;

/** Renders LLM-authored markdown (GitHub-flavored) at the conversation bubble's compact scale. */
export function MarkdownBlock({ children }: MarkdownBlockProps): ReactElement {
    return (
        <MarkdownScope>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
        </MarkdownScope>
    );
}
